import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { screensApi } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';
import { formatNumber, formatDate } from '../lib/formatters.js';
import { formatDimensionValue, formatDimensionName } from '../lib/dimension-labels.js';
import { useDateRange } from '../hooks/useDateRange.js';
import { DateRangeSelector } from '../components/DateRangeSelector.js';

/**
 * @graph-node {Page}
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business スクリーン詳細分析。日次メトリクストレンド・イベント内訳棒グラフ・ディメンション別ユーザー分布を可視化
 * @graph-connects screensApi [calls] Screens APIクライアント呼び出し
 * @graph-connects queryKeys [calls] クエリキー生成
 * @graph-connects useDateRange [calls] 日付範囲管理フック
 * @graph-connects formatNumber [calls] 数値フォーマット
 * @graph-connects formatDate [calls] 日付フォーマット
 * @graph-connects formatDimensionValue [calls] ディメンション値ラベル変換
 * @graph-connects formatDimensionName [calls] ディメンション名ラベル変換
 * @graph-connects DateRangeSelector [calls] 日付範囲セレクター描画
 * @graph-connects TabSelector [calls] タブ切り替えUI描画
 * @graph-connects MetricsPanel [calls] メトリクスチャートパネル描画
 * @graph-connects EventsPanel [calls] イベント内訳パネル描画
 * @graph-connects DimensionsPanel [calls] ディメンション内訳パネル描画
 */
export const Route = createFileRoute('/screens/$screenName')({
  component: ScreenDetailPage,
});

// ============================================================
// タブ
// ============================================================

type Tab = 'metrics' | 'events' | 'dimensions';

/** @graph-connects none */
function TabSelector({ active, onChange }: { active: Tab; onChange: (tab: Tab) => void }) {
  const { t } = useTranslation();
  const tabs: { key: Tab; labelKey: string }[] = [
    { key: 'metrics', labelKey: 'screens.metricsTab' },
    { key: 'events', labelKey: 'screens.eventsTab' },
    { key: 'dimensions', labelKey: 'screens.dimensionsTab' },
  ];

  return (
    <div className="flex gap-1 p-1 rounded-lg bg-[var(--color-bg-hover)]">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          onClick={() => {
            onChange(tab.key);
          }}
          className={`
            px-4 py-2 rounded-md text-sm font-medium transition-colors
            ${
              active === tab.key
                ? 'bg-[var(--color-accent-primary)] text-white shadow-sm'
                : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'
            }
          `}
        >
          {t(tab.labelKey)}
        </button>
      ))}
    </div>
  );
}

// ============================================================
// メトリクスチャート
// ============================================================

/** @graph-connects ux-insights-api [calls] UX Insights APIを呼び出す */
function MetricsPanel({
  screenName,
  startDate,
  endDate,
}: {
  screenName: string;
  startDate: string;
  endDate: string;
}) {
  const { t } = useTranslation();

  const metricsQuery = useQuery({
    queryKey: queryKeys.screens.metrics(screenName, startDate, endDate),
    queryFn: () => screensApi.getMetrics(screenName, startDate, endDate),
    staleTime: 5 * 60 * 1000,
  });

  if (metricsQuery.isLoading) {
    return <div className="skeleton-shimmer h-64 w-full rounded-lg" />;
  }

  return (
    <div className="card animate-fade-in-up">
      <h3 className="text-sm font-semibold text-[var(--color-text-primary)] mb-4">
        {t('screens.dailyTrend')}
      </h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={metricsQuery.data?.data ?? []}
            margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id="gradMetricEvents" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--chart-1)" stopOpacity={0.2} />
                <stop offset="95%" stopColor="var(--chart-1)" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gradMetricUsers" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--chart-2)" stopOpacity={0.2} />
                <stop offset="95%" stopColor="var(--chart-2)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
            <XAxis
              dataKey="date"
              tickFormatter={formatDate}
              tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
              axisLine={{ stroke: 'var(--color-border)' }}
              tickLine={false}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v: number) => formatNumber(v)}
              width={60}
            />
            <Tooltip
              labelFormatter={formatDate as never}
              formatter={
                ((value: number, name: string) => [
                  formatNumber(value),
                  name === 'eventCount' ? t('screens.eventCount') : t('screens.uniqueUsers'),
                ]) as never
              }
              contentStyle={{
                backgroundColor: 'var(--color-bg-elevated)',
                border: '1px solid var(--color-border)',
                borderRadius: '8px',
                fontSize: '12px',
              }}
            />
            <Area
              type="monotone"
              dataKey="eventCount"
              stroke="var(--chart-1)"
              strokeWidth={2}
              fill="url(#gradMetricEvents)"
              dot={false}
            />
            <Area
              type="monotone"
              dataKey="uniqueUsers"
              stroke="var(--chart-2)"
              strokeWidth={2}
              fill="url(#gradMetricUsers)"
              dot={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ============================================================
// イベント内訳
// ============================================================

/** @graph-connects ux-insights-api [calls] UX Insights APIを呼び出す */
function EventsPanel({
  screenName,
  startDate,
  endDate,
}: {
  screenName: string;
  startDate: string;
  endDate: string;
}) {
  const { t } = useTranslation();

  const eventsQuery = useQuery({
    queryKey: queryKeys.screens.events(screenName, startDate, endDate),
    queryFn: () => screensApi.getEvents(screenName, startDate, endDate),
    staleTime: 5 * 60 * 1000,
  });

  if (eventsQuery.isLoading) {
    return <div className="skeleton-shimmer h-64 w-full rounded-lg" />;
  }

  const data = eventsQuery.data?.data ?? [];

  return (
    <div className="card animate-fade-in-up">
      <h3 className="text-sm font-semibold text-[var(--color-text-primary)] mb-4">
        {t('screens.eventBreakdown')}
      </h3>
      {data.length > 0 ? (
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              layout="vertical"
              margin={{ top: 0, right: 16, left: 0, bottom: 0 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="var(--color-border)"
                horizontal={false}
              />
              <XAxis
                type="number"
                tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
                axisLine={{ stroke: 'var(--color-border)' }}
                tickLine={false}
                tickFormatter={(v: number) => formatNumber(v)}
              />
              <YAxis
                type="category"
                dataKey="event"
                tick={{ fontSize: 11, fill: 'var(--color-text-secondary)' }}
                axisLine={false}
                tickLine={false}
                width={140}
              />
              <Tooltip
                formatter={((value: number) => formatNumber(value)) as never}
                contentStyle={{
                  backgroundColor: 'var(--color-bg-elevated)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                  fontSize: '12px',
                }}
              />
              <Bar dataKey="eventCount" fill="var(--chart-3)" radius={[0, 4, 4, 0]} barSize={20} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p className="text-sm text-[var(--color-text-muted)] text-center py-8">
          {t('common.noData')}
        </p>
      )}
    </div>
  );
}

// ============================================================
// ディメンション内訳
// ============================================================

/** @graph-connects ux-insights-api [calls] UX Insights APIを呼び出す */
function DimensionsPanel({
  screenName,
  startDate,
  endDate,
}: {
  screenName: string;
  startDate: string;
  endDate: string;
}) {
  const { t } = useTranslation();
  const [selectedDimension, setSelectedDimension] = useState<string | null>(null);

  // 利用可能ディメンション一覧を取得
  const availableQuery = useQuery({
    queryKey: queryKeys.screens.availableDimensions(screenName, startDate, endDate),
    queryFn: () => screensApi.getAvailableDimensions(screenName, startDate, endDate),
    staleTime: 5 * 60 * 1000,
  });

  const availableDimensions = availableQuery.data?.dimensions ?? [];
  const activeDimension =
    selectedDimension ?? (availableDimensions[0] as string | undefined) ?? 'device_category';

  const dimensionsQuery = useQuery({
    queryKey: queryKeys.screens.dimensions(screenName, startDate, endDate, activeDimension),
    queryFn: () => screensApi.getDimensions(screenName, startDate, endDate, activeDimension),
    staleTime: 5 * 60 * 1000,
    enabled: availableDimensions.length > 0 || selectedDimension !== null,
  });

  const data = (dimensionsQuery.data?.data ?? []).map((row) => ({
    ...row,
    dimensionValue: formatDimensionValue(activeDimension, row.dimensionValue),
  }));

  return (
    <div className="card animate-fade-in-up">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2">
        <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
          {t('screens.dimensionBreakdown')}
        </h3>
        {availableQuery.isLoading ? (
          <div className="skeleton-shimmer h-8 w-40 rounded-lg" />
        ) : (
          <select
            value={activeDimension}
            onChange={(e) => {
              setSelectedDimension(e.target.value);
            }}
            className="px-3 py-1.5 rounded-lg bg-[var(--color-bg-hover)] text-[var(--color-text-primary)] text-xs border border-[var(--color-border)] focus:border-[var(--color-border-focus)] focus:outline-none"
          >
            {availableDimensions.map((dim) => (
              <option key={dim} value={dim}>
                {formatDimensionName(dim)}
              </option>
            ))}
          </select>
        )}
      </div>

      {dimensionsQuery.isLoading ? (
        <div className="skeleton-shimmer h-48 w-full rounded-lg" />
      ) : data.length > 0 ? (
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 0, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="var(--color-border)"
                horizontal={false}
              />
              <XAxis
                dataKey="dimensionValue"
                tick={{ fontSize: 10, fill: 'var(--color-text-muted)' }}
                axisLine={{ stroke: 'var(--color-border)' }}
                tickLine={false}
                interval={0}
                angle={-20}
                textAnchor="end"
                height={40}
              />
              <YAxis
                tick={{ fontSize: 10, fill: 'var(--color-text-muted)' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v: number) => formatNumber(v)}
                width={50}
              />
              <Tooltip
                formatter={((value: number) => formatNumber(value)) as never}
                contentStyle={{
                  backgroundColor: 'var(--color-bg-elevated)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                  fontSize: '12px',
                }}
              />
              <Bar dataKey="uniqueUsers" fill="var(--chart-5)" radius={[4, 4, 0, 0]} barSize={24} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p className="text-sm text-[var(--color-text-muted)] text-center py-8">
          {t('common.noData')}
        </p>
      )}
    </div>
  );
}

// ============================================================
// メインページ
// ============================================================

/** @graph-connects none */
function ScreenDetailPage() {
  const { t } = useTranslation();
  const { screenName } = useParams({ from: '/screens/$screenName' });
  const { preset, setPreset, startDate, endDate, setCustomDates } = useDateRange('30d');
  const [activeTab, setActiveTab] = useState<Tab>('metrics');

  return (
    <div className="space-y-6">
      {/* ナビゲーション */}
      <div>
        <Link
          to="/screens"
          className="text-sm text-[var(--color-accent-primary)] hover:text-[var(--color-accent-hover)] transition-colors"
        >
          ← {t('screens.backToList')}
        </Link>
      </div>

      {/* ページヘッダー */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">{screenName}</h1>
          <p className="text-sm text-[var(--color-text-muted)]">{t('screens.detailTitle')}</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <TabSelector active={activeTab} onChange={setActiveTab} />
          <DateRangeSelector
            preset={preset}
            startDate={startDate}
            endDate={endDate}
            onPresetChange={setPreset}
            onCustomDatesChange={setCustomDates}
          />
        </div>
      </div>

      {/* タブコンテンツ */}
      {activeTab === 'metrics' && (
        <MetricsPanel screenName={screenName} startDate={startDate} endDate={endDate} />
      )}
      {activeTab === 'events' && (
        <EventsPanel screenName={screenName} startDate={startDate} endDate={endDate} />
      )}
      {activeTab === 'dimensions' && (
        <DimensionsPanel screenName={screenName} startDate={startDate} endDate={endDate} />
      )}
    </div>
  );
}
